import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { TextOneLine } from '@actual-app/components/text-one-line';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import type {
  CategoryEntity,
  CategoryGroupEntity,
} from '@actual-app/core/types/models';

import { SelectorCategorias } from '#components/mobile/budget/FiltrosCategorias';
import { useCuentasCategorias } from '#components/mobile/budget/useCuentasCategorias';
import { useDatosObjetivos } from '#components/mobile/budget/useDatosObjetivos';
import { usePagosFijos } from '#components/mobile/budget/usePagosFijos';
import {
  AccionTexto,
  Fila,
  Tarjeta,
  VacioCompacto,
} from '#components/mobile/inicio/comunes';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import {
  categoriasCuentaComun,
  cuentasDelTraspaso,
  gastoPrevisto,
  ingresoMesSiguiente,
  leerCategoriasComunes,
  leerTraspasoDelMes,
  marcarTraspasado,
  MESES_PREVISTO,
} from '#components/mobile/inicio/cuentaComun';
import {
  leerDuenosNominas,
  nominasDeLaCuenta,
  ponerDuenoNomina,
} from '#components/mobile/inicio/dosNominas';
import { estilos } from '#components/mobile/inicio/inicio.estilos';
import { BodegaDosNominas } from '#components/mobile/inicio/widgets/BodegaDosNominas';
import {
  useDatosCuentaComun,
  useIngresosCuentaComun,
} from '#components/mobile/inicio/widgets/useDatosCuentaComun';
import { Boton } from '#components/mobile/ui/Boton';
import { Pildora } from '#components/mobile/ui/Pildora';
import { color, espacio, radio } from '#components/mobile/ui/tokens';
import { useTripulacion } from '#components/pareja/useTripulacion';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useAccounts } from '#hooks/useAccounts';
import { useCategories } from '#hooks/useCategories';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useSheetValue } from '#hooks/useSheetValue';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { accountBalance, hastaHoy } from '#spreadsheet/bindings';

/**
 * «Cuenta común»: cuánto ingresar para el MES SIGUIENTE (Vadym cobra hacia
 * el 28 y entonces asigna el mes que viene). Cifra grande y debajo la cuenta:
 * asignado el mes siguiente (negativos incluidos) − saldo de hoy + lo que aún
 * queda este mes en esas categorías. «Traspasado» (pref `cuenta-comun-traspasado`, por mes destino) deja el
 * bloque en «Cubierto» hasta el día 1, cuando pasa solo al mes siguiente.
 *
 * Tamaños: compacto = cifra y botón; normal = + la cuenta; grande = +
 * desglose por categoría del mes siguiente.
 */
export function CuentaComun({ tamano, month }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const [raw, setRaw] = useSyncedPref('cuenta-comun-categorias');
  const manuales = useMemo(() => leerCategoriasComunes(raw), [raw]);
  const [eligiendo, setEligiendo] = useState(false);

  const siguiente = monthUtils.addMonths(month, 1);
  const nombreMes = (mes: string) => monthUtils.format(mes, 'MMMM', locale);

  const { data: cuentas = [] } = useAccounts();
  const destino = useMemo(() => cuentasDelTraspaso(cuentas).destino, [cuentas]);
  const {
    data: { list: categorias, grouped } = {
      list: [] as CategoryEntity[],
      grouped: [] as CategoryGroupEntity[],
    },
  } = useCategories();
  // Las mismas categorías que el filtro de la cuenta común en el Plan (con
  // las que solo mueven dinero, como «Moviments»), más las de los pagos
  // fijos cargados en ella; la elección manual queda de respaldo.
  const { pagos } = usePagosFijos(month);
  const { cuentaDe } = useCuentasCategorias(month);
  const { ids, automaticas } = useMemo(() => {
    const deLaCuenta = destino
      ? grouped
          .filter(g => !g.hidden && !g.is_income)
          .flatMap(g => g.categories ?? [])
          .filter(c => !c.hidden && cuentaDe(c.id) === destino.id)
          .map(c => c.id)
      : [];
    return categoriasCuentaComun({
      pagos,
      cuentaComunId: destino?.id,
      manuales,
      deLaCuenta,
    });
  }, [pagos, destino, manuales, grouped, cuentaDe]);

  // En el orden del Plan.
  const elegidas = useMemo(() => {
    const porId = new Map(categorias.map(c => [c.id, c]));
    const orden = grouped.flatMap(g => (g.categories ?? []).map(c => c.id));
    const posicion = new Map(orden.map((id, i) => [id, i]));
    return ids
      .map(id => porId.get(id))
      .filter((c): c is CategoryEntity => Boolean(c))
      .sort(
        (a, b) =>
          (posicion.get(a.id) ?? Infinity) - (posicion.get(b.id) ?? Infinity),
      );
  }, [categorias, grouped, ids]);

  const datosMes = useDatosObjetivos(month, elegidas);
  const datosSiguiente = useDatosObjetivos(siguiente, elegidas);

  const saldo = useSheetValue<'account', 'balance'>(
    hastaHoy(accountBalance(destino?.id ?? '')),
  );
  const { data: movimientos } = useDatosCuentaComun(
    destino?.id,
    month,
    MESES_PREVISTO,
  );
  const { previsto } = useMemo(
    () =>
      gastoPrevisto(
        movimientos?.salidas ?? {},
        monthUtils.rangeInclusive(
          monthUtils.subMonths(month, MESES_PREVISTO),
          monthUtils.subMonths(month, 1),
        ),
      ),
    [movimientos, month],
  );

  const calculo = ingresoMesSiguiente({
    asignadoSiguiente: elegidas.map(
      c => datosSiguiente.get(c.id)?.budgeted ?? 0,
    ),
    disponibleEsteMes: elegidas.map(c => datosMes.get(c.id)?.balance ?? 0),
    saldo: saldo ?? 0,
    previsto,
  });

  const [rawTraspasado, setTraspasado] = useSyncedPref(
    'cuenta-comun-traspasado',
  );
  const traspaso = leerTraspasoDelMes(rawTraspasado, siguiente);
  const cubierto =
    traspaso.traspasado || (calculo.necesita > 0 && calculo.ingresar === 0);

  // Pareja: si en la cuenta común entran dos nóminas, la bodega sustituye
  // a «Ingresa para…» (con una sola, todo sigue igual).
  const tripulantes = useTripulacion();
  const { data: ingresos } = useIngresosCuentaComun(destino?.id, month);
  const nominas = useMemo(
    () => nominasDeLaCuenta(ingresos ?? [], month).slice(0, 2),
    [ingresos, month],
  );
  const [rawDuenos, setDuenos] = useSyncedPref('cuenta-comun-nominas');

  const elegir = () => setEligiendo(true);
  // Con categorías automáticas no se eligen a mano: mandan los pagos fijos.
  const accion = automaticas ? undefined : (
    <AccionTexto onPress={elegir}>
      <Trans>Categories</Trans>
    </AccionTexto>
  );
  // Una sola hoja: marcar y desmarcar, y se guarda todo de una vez.
  const selector = eligiendo && (
    <SelectorCategorias
      titulo={t('Joint account categories')}
      grupos={grouped}
      seleccion={manuales}
      onGuardar={nuevos => {
        setRaw(JSON.stringify(nuevos));
        setEligiendo(false);
      }}
      onCerrar={() => setEligiendo(false)}
    />
  );

  if (elegidas.length === 0) {
    return (
      <Tarjeta
        titulo={t('Joint account')}
        sinTarjeta
        data-testid="inicio-cuenta-comun"
      >
        <VacioCompacto
          texto={t('No categories for the joint account yet')}
          accion={<Trans>Choose categories</Trans>}
          onAccion={elegir}
          data-testid="cuenta-comun-vacio"
        />
        {selector}
      </Tarjeta>
    );
  }

  const listaCategorias = tamano === 'grande' && (
    <View>
      {elegidas.map(c => (
        <Fila key={c.id} separada style={{ minHeight: 48 }}>
          <TextOneLine
            style={{ ...estilos.filaTitulo, flex: 1, fontWeight: 700 }}
          >
            {c.name}
          </TextOneLine>
          <PrivacyFilter>
            <Pildora estado="neutro">
              {format(datosSiguiente.get(c.id)?.budgeted ?? 0, 'financial')}
            </Pildora>
          </PrivacyFilter>
        </Fila>
      ))}
    </View>
  );

  if (nominas.length >= 2) {
    return (
      <Tarjeta
        titulo={t('Joint account · {{month}}', { month: nombreMes(siguiente) })}
        accion={accion}
        data-testid="inicio-cuenta-comun"
      >
        <BodegaDosNominas
          nominas={nominas}
          duenos={leerDuenosNominas(rawDuenos)}
          tripulantes={tripulantes}
          llega={calculo.llega}
          necesita={calculo.necesita}
          mes={nombreMes(siguiente)}
          onDueno={(payee, persona) =>
            setDuenos(ponerDuenoNomina(rawDuenos, payee, persona))
          }
        />
        {listaCategorias}
        {selector}
      </Tarjeta>
    );
  }

  const marcar = (valor: boolean) =>
    setTraspasado(
      marcarTraspasado(rawTraspasado, siguiente, valor, calculo.ingresar),
    );

  return (
    <Tarjeta
      titulo={t('Joint account')}
      accion={accion}
      data-testid="inicio-cuenta-comun"
    >
      <Fila style={{ minHeight: 0, alignItems: 'flex-end' }}>
        <View style={estilos.filaTexto}>
          <Text style={estilos.etiquetaPequena}>
            {cubierto
              ? mayuscula(nombreMes(siguiente))
              : calculo.estimado
                ? t('Deposit for {{month}} · estimate', {
                    month: nombreMes(siguiente),
                  })
                : t('Deposit for {{month}}', { month: nombreMes(siguiente) })}
          </Text>
          {cubierto ? (
            <Text
              style={{ ...estilos.cifra, fontSize: 28, color: color.ok }}
              data-testid="cuenta-comun-importe"
            >
              {t('✓ Covered')}
            </Text>
          ) : (
            <PrivacyFilter>
              <Text
                style={{ ...estilos.cifra, fontSize: 28 }}
                data-testid="cuenta-comun-importe"
              >
                {format(calculo.ingresar, 'financial')}
              </Text>
            </PrivacyFilter>
          )}
        </View>
        {traspaso.traspasado ? (
          <Boton
            variante="fantasma"
            aria-pressed
            data-testid="cuenta-comun-traspasado"
            onPress={() => marcar(false)}
          >
            <Trans>Undo</Trans>
          </Boton>
        ) : (
          !cubierto && (
            <Boton
              aria-pressed={false}
              data-testid="cuenta-comun-traspasado"
              onPress={() => marcar(true)}
            >
              <Trans>Transferred</Trans>
            </Boton>
          )
        )}
      </Fila>

      {cubierto ? (
        <View style={{ gap: espacio.fila }}>
          <PrivacyFilter>
            <Caja tono="ok" data-testid="cuenta-comun-cubierto">
              {traspaso.traspasado && traspaso.importe !== null
                ? t('You transferred {{amount}} for {{month}}.', {
                    amount: format(traspaso.importe, 'financial'),
                    month: nombreMes(siguiente),
                  })
                : t('{{month}} gets {{arrives}} for {{needed}} needed.', {
                    month: nombreMes(siguiente),
                    arrives: format(calculo.llega, 'financial'),
                    needed: format(calculo.necesita, 'financial'),
                  })}
            </Caja>
          </PrivacyFilter>
          <Text style={estilos.filaSub}>
            {t('On the 1st this block moves to {{month}}.', {
              month: nombreMes(monthUtils.addMonths(siguiente, 1)),
            })}
          </Text>
        </View>
      ) : (
        calculo.corto && (
          <PrivacyFilter>
            <Caja tono="aviso" data-testid="cuenta-comun-corto">
              {t(
                'You assigned {{assigned}} and usually spend {{usual}}. To cover the usual, deposit {{deposit}}.',
                {
                  assigned: format(calculo.asignadoGasto, 'financial'),
                  usual: format(previsto, 'financial'),
                  deposit: format(calculo.ingresarHabitual, 'financial'),
                },
              )}
            </Caja>
          </PrivacyFilter>
        )
      )}

      {!cubierto && tamano !== 'compacto' && (
        <PrivacyFilter>
          <View
            style={{
              gap: espacio.fila,
              borderTop: `1px solid ${color.line}`,
              paddingTop: 12,
            }}
            data-testid="cuenta-comun-cuenta"
          >
            <Linea
              etiqueta={
                calculo.estimado
                  ? t('Usual spending')
                  : t('Assigned for {{month}}', { month: nombreMes(siguiente) })
              }
              nota={
                calculo.estimado
                  ? t('Not assigned for {{month}} yet', {
                      month: nombreMes(siguiente),
                    })
                  : previsto > 0
                    ? t('You usually spend {{amount}}', {
                        amount: format(previsto, 'financial'),
                      })
                    : undefined
              }
              valor={format(calculo.necesita, 'financial')}
            />
            <Linea
              etiqueta={t('Balance today')}
              valor={`− ${format(saldo ?? 0, 'financial')}`}
            />
            <Linea
              etiqueta={t('Left in your categories in {{month}}', {
                month: nombreMes(month),
              })}
              valor={`+ ${format(calculo.pendiente, 'financial')}`}
            />
            <Linea
              total
              etiqueta={t('To deposit')}
              valor={format(calculo.ingresar, 'financial')}
            />
          </View>
        </PrivacyFilter>
      )}

      {listaCategorias}

      {selector}
    </Tarjeta>
  );
}

function mayuscula(texto: string) {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function Caja({
  tono,
  children,
  'data-testid': testId,
}: {
  tono: 'ok' | 'aviso';
  children: ReactNode;
  'data-testid'?: string;
}) {
  return (
    <Text
      data-testid={testId}
      style={{
        display: 'block',
        borderRadius: radio.boton,
        padding: '10px 12px',
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1.45,
        backgroundColor: tono === 'ok' ? color.okSoft : color.warnSoft,
        color: tono === 'ok' ? color.ok : color.warn,
      }}
    >
      {children}
    </Text>
  );
}

function Linea({
  etiqueta,
  nota,
  valor,
  total = false,
}: {
  etiqueta: string;
  nota?: string;
  valor: string;
  total?: boolean;
}) {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: espacio.icono,
        ...(total
          ? { borderTop: `1px dashed ${color.line2}`, paddingTop: 8 }
          : null),
      }}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: total ? 15 : 14,
            fontWeight: total ? 800 : 600,
            color: total ? color.fg : color.fg2,
          }}
        >
          {etiqueta}
        </Text>
        {nota && (
          <Text style={{ fontSize: 12, fontWeight: 600, color: color.fg3 }}>
            {nota}
          </Text>
        )}
      </View>
      <Text
        style={{
          ...estilos.resumenValor,
          fontSize: total ? 15 : 14,
        }}
      >
        {valor}
      </Text>
    </View>
  );
}
